<template>
    <div
        class="cz2__two_pack_selector--wrapper"
        role="region"
        aria-label="Building your Custom Pop"
    >
        <!-- SPELLING BEE START: Borders and logos -->
        <template v-if="isSpellingBee">
            <div class="cz2__sb-border" />
            <div class="cz2__sb-background" />

            <div
                class="cz2__sb-logo-m-two-pack"
                @click.prevent="tapLogo"
            />

            <div
                class="cz2__sb-logo-d-two-pack"
                @click.prevent="tapLogo"
            />

            <div
                class="cz2__sb-logo-k-two-pack"
                @click.prevent="tapLogo"
            />
        </template>
        <!-- SPELLING BEE END -->

        <splash-header
            v-if="!isKiosk && !isMariners"
            :has-video="splashVideoUrl !== null"
        />

        <ul
            class="cz2__two_pack_selector"
            :class="['cz2__two_pack_selector__content', {
                'is-mobile': isMobile,
            }]"
        >
            <li
                ref="onePackButton"
                class="cz2__two_pack_selector__button-li"
            >
                <two-pack-selector-card
                    @click.native="triggerStart('one-pack')"
                    :pack-image="onePackImage"
                    :banner-copy="$t('ONE_PACK')"
                    :content-copy="$t('PACK_CARD_COPY')"
                    :price-copy="getPriceCopy(onePackPrice)"
                    :cta-copy="$t('BUILD_POP')"
                />
            </li>

            <li
                ref="twoPackButton"
                class="cz2__two_pack_selector__button-li"
            >
                <two-pack-selector-card
                    @click.native="triggerStart('two-pack')"
                    :pack-image="twoPackImage"
                    :banner-copy="$t('TWO_PACK')"
                    :content-copy="$t('2_PACK_CARD_COPY')"
                    :price-copy="getPriceCopy(twoPackPrice)"
                    :cta-copy="$t('BUILD_2_PACK')"
                />
            </li>
        </ul>

        <banner-ad v-if="!isMariners" />

        <splash-video
            v-if="!isKiosk && !isMariners"
            :splash-video-url="splashVideoUrl"
        />

        <!-- <img
            src="../assets/images/toty-logo.png"
            :alt="$t('LOGO_TOTY')"
            class="cz2__toty-logo"
        > -->
    </div>
</template>

<script>
    import { animate } from 'motion';

    import { formatPrice } from '../helpers/formatPrice';

    import SplashHeader from './SplashHeader.vue';
    import MainNavigationListButton from './MainNavigationListButton.vue';
    import RedBannerButton from './RedBannerButton.vue';
    import Btn from './ui/Btn.vue';
    import SbButton from './SbButton.vue';
    import SbBannerButton from './SbBannerButton.vue';
    import TwoPackSelectorCard from './TwoPackSelectorCard.vue';
    import BannerAd from './BannerAd.vue';
    import SplashVideo from './SplashVideo.vue';

    export default {
        name: 'TwoPackSelector',

        components: {
            SplashHeader,
            Btn,
            MainNavigationListButton,
            SbBannerButton,
            RedBannerButton,
            SbButton,
            TwoPackSelectorCard,
            BannerAd,
            SplashVideo,
        },

        computed: {
            globalSettings() {
                return this.$store.state.customizer.globalSettings?.blueprint?.custom;
            },

            globalSettingsController() {
                return this.$store.state.customizer.globalSettings;
            },

            onePackImage() {
                // const newImage = this.globalSettings?.['fun-splash-one-pack-image-new'];
                const oldImage = this.globalSettings?.['fun-splash-one-pack-image'];

                return `${oldImage}?fmt=webp&qlt=70`;
            },

            twoPackImage() {
                // const newImage = this.globalSettings?.['fun-splash-two-pack-image-new'];
                const oldImage = this.globalSettings?.['fun-splash-two-pack-image'];

                return `${oldImage}?fmt=webp&qlt=70`;
            },

            isSpellingBee() {
                return this.$store.state.isSpellingBee;
            },

            isMariners() {
                return this.$store.state.isMariners;
            },

            isMobile() {
                return this.$store.state.customizer.isMobile;
            },

            isKiosk() {
                return this.$store.state.customizer.options.isKiosk;
            },

            onePackPrice() {
                // Fallback to override.
                if (this.globalSettings?.['fun-one-pack-price']?.length > 0) {
                    return this.globalSettings?.['fun-one-pack-price'];
                }

                const sku = this.globalSettings?.['fun-one-pack-price-sku'];
                if (sku?.length > 0) {
                    const price = this.getPrice(sku);
                    if (price != null) {
                        return price;
                    }
                }

                return '';
            },

            twoPackPrice() {
                // Fallback to override.
                if (this.globalSettings?.['fun-two-pack-price']?.length > 0) {
                    return this.globalSettings?.['fun-two-pack-price'];
                }

                const sku = this.globalSettings?.['fun-two-pack-price-sku'];
                if (sku?.length > 0) {
                    const price = this.getPrice(sku);
                    if (price != null) {
                        return price;
                    }
                }

                return '';
            },

            splashVideoUrl() {
                const url = this.globalSettings?.['fun-splash-video'];

                if (!url || url === 'none') {
                    return null;
                }

                return url;
            },
        },

        mounted() {
            this.animateButtons();
        },

        methods: {
            getPrice(sku) {
                // eslint-disable-next-line prefer-destructuring
                const state = this.$store.state;

                if (state.ecommerce.products && state.ecommerce.products[sku]) {
                    const product = state.ecommerce.products[sku];

                    return product.price;
                }

                if (state.options.alwaysAvailable) {
                    return 10;
                }

                return null;
            },

            animateButtons() {
                const { onePackButton, twoPackButton } = this.$refs;

                [onePackButton, twoPackButton].forEach((button) => {
                    animate(button, {
                        transform: [
                            'scale(0)',
                            'scale(0.6)',
                            'scale(1)',
                            'scale(1.05)',
                            'scale(1)',
                            'scale(0.98)',
                            'scale(1)',
                        ],
                    }, {
                        duration: 1,
                        easing: 'ease-in-out',
                    });
                });
            },

            tapLogo() {
                this.$store.dispatch('tapLogo');
            },

            showAiModal(packType) {
                if (this.isKiosk) {
                    this.$emit('start', packType);
                } else if (this.globalSettings['fun-ai-selfie'] === 'true') {
                    // Store pack type so the AI modal can load the right experience.
                    this.$store.dispatch('setAiTrigger', true);

                    this.$emit('start', packType);
                } else {
                    this.$emit('start', packType);
                }
            },

            triggerStart(packType) {
                this.$emit('start', packType);
            },

            getPriceCopy(price) {
                return this.$t('PACK_CARD_PRICE').replace('{0}', formatPrice(this.$store.state, price, 'standard'));
            },

            emitStart(packType) {
                this.$emit('start', packType);
            },
        },
    };
</script>

<style lang="scss">
    @import '../style/variables';

    #cz2.cz2 {
        .cz2__sb-logo-m-two-pack, .cz2__sb-logo-d-two-pack, .cz2__sb-logo-k-two-pack {
            display: none;
        }

        .cz2__two_pack_selector--wrapper {
            position: absolute;

            // top: 96px;
            top: 50%;
            // 96px is the logos height + 16px margin
            transform: translateY(calc(-50% - 96px));
            left: 0;
            right: 0;
            bottom: 0;

            width: 100%;

            display: flex;
            flex-direction: column;
            align-items: center;
        }

        .cz2__two_pack_selector {
            margin-top: 0;
            padding: 0 32px;
            display: flex;
            justify-content: center;
            align-items: stretch;
            background: rgba(0, 0, 0, 0);

            .cz2__toty-logo {
                top: 4px;
                right: 4px;
            }
        }

        .cz2__two_pack_selector__content {
            width: 100%;
            display: flex;
            flex-direction: column;

            justify-content: center;
            align-items: center;

            gap: 12px;

            @include respond-above(lg) {
                flex-direction: row;

                gap: 64px;
            }
        }

        .cz2__two_pack_selector__button-li {
            width: 100%;
        }

        .cz2__btn--two-pack-red-banner {
            max-width: 260px;
            height: 56px;
            display: flex;
            align-items: center;
        }

        @include respond-above(lg) {
            .cz2__two_pack_selector--wrapper {
                top: 200px;
                transform: none;
            }

            .cz2__two_pack_selector__button {
                // width: 450px;

                .cz2__btn--secondary {
                    margin-top: 28px;
                    background: white;
                    color: black;
                }
            }

            .cz2__btn--two-pack-red-banner {
                max-width: none;
                font-size: 64px;
                height: 93px;
            }
        }

        @include respond-above(md) {
            .cz2__two_pack_selector__button-li {
                width: fit-content;
                height: 100%;
            }
        }
    }

    #cz2.cz2.cz2__is-kiosk {
        .cz2__two_pack_selector--wrapper {
            overflow: hidden;
            justify-content: center;
        }

        .cz2__two_pack_selector__content {
            flex-direction: column;
        }

        .cz2__two_pack_selector {
            .cz2__toty-logo {
                top: 4px;
                left: 4px;
                width: 239px;
                height: 239px;
            }
        }
    }

    #cz2.cz2__is-mariners {
        // Allow Funko logo to be clicked through elements that overlap it
        .cz2__two_pack_selector {
            pointer-events: none;
        }

        .cz2__two_pack_selector__content {
            pointer-events: auto;
        }

        @include respond-above(lg) {
            .cz2__two_pack_selector {
                padding: 0 64px;
            }
        }

        @include respond-above(xl) {
            .cz2__two_pack_selector {
                padding: 0 100px;
            }
        }
    }
</style>
